import { EnumType } from 'src/core/types/enum';
import { SelectOption } from 'src/design_system/components/select/types';
import { PlanType } from 'src/modules/usage/types/billing';
import { UserStatusEnums } from '../../shared/types/status';

/**
 * Known billing plan names. Values must match backend/API plan names.
 */
export enum PlanName {
  ENTERPRISE = 'Enterprise',
  PROFESSIONAL = 'Professional',
  STARTER = 'Starter',
  EARLY_ACCESS = 'Early Access',
  PAY_AS_YOU_GO = 'Pay As You Go',
  LITE = 'Lite',
  TRIAL = 'Trial',
}

/**
 * Form mode for customer forms.
 */
export enum CustomerFormMode {
  CREATE = 'create',
  EDIT = 'edit',
}

/**
 * MFA configuration options for customers.
 * Values must match backend enum names (uppercase).
 */
export enum MFAConfiguration {
  DISABLED = 'DISABLED',
  OPTIONAL = 'OPTIONAL',
  MANDATORY = 'MANDATORY',
}

/**
 * Helper to normalize MFA configuration value from backend.
 * Backend may return uppercase (DISABLED) or capitalized (Disabled) depending on serialization.
 */
export function normalizeMFAConfiguration(
  value: string | null | undefined
): MFAConfiguration {
  if (!value) return MFAConfiguration.DISABLED;
  const upper = value.toUpperCase();
  if (upper === 'OPTIONAL') return MFAConfiguration.OPTIONAL;
  if (upper === 'MANDATORY') return MFAConfiguration.MANDATORY;
  return MFAConfiguration.DISABLED;
}

/**
 * Represents the type of a customer in the customer table.
 */
export interface CustomerType {
  id?: number;
  entity_name: string;
  status: string;
  mfa_configuration?: MFAConfiguration;
  users_count?: number;
  plan: PlanType;
  usage_consumption: number;
  updated_at: string;
  created_at: string;
  commodities: string[];
  deactivated_at: string;
  add_on: number;
  enterprise_bundle: boolean;
  crop_extraction_enabled: boolean;
  account_type: AccountType;
  test_profiles: string[];
  custom_consumption_limit: number | null;
  /** Data preparation (VP-3067): the customer's stored access toggle; false when unset. */
  data_preparation_access?: boolean;
  /** Data preparation (VP-3067): cumulative source data used, in bytes; 0 when never used. */
  data_preparation_used_bytes?: number;
  /** Data preparation (VP-3067): usage is at or above the effective limit; never true when unlimited. */
  data_preparation_limit_reached?: boolean;
}

/**
 * What a customer account actually is. Mirrors the backend's AccountType enum and
 * the customer_account_type Postgres type; the API rejects any other value.
 *
 * CUSTOMER was previously REAL, and a single TEST value split into the two below:
 * an account we run internally is a different thing, for analysis, from a sandbox
 * held by someone outside the company. Display labels live in i18n under
 * `customers.account_types` -- these are wire values, never shown to users.
 */
export enum AccountType {
  CUSTOMER = 'CUSTOMER',
  DEMO = 'DEMO',
  INTERNAL_TEST = 'INTERNAL_TEST',
  EXTERNAL_SANDBOX = 'EXTERNAL_SANDBOX',
}

/**
 * Form-side shape for the account type radio group. CustomerRadioGroupField matches
 * and emits whole option objects, so the form holds one of these and the request
 * payload unwraps `.value` -- same arrangement as `plan` / PlanSelectOption.
 */
export type AccountTypeOption = {
  label: string;
  value: AccountType;
};

export type PlanSelectOption = SelectOption & {
  size_limit: number;
  requires_custom_limit?: boolean;
};

export type CustomerFormBaseType = {
  plan?: PlanSelectOption;
  company?: string;
  plan_effective_date?: Date;
  status?: UserStatusEnums;
  commodities?: EnumType[];
  deactivated_at?: string;
  custom_consumption_limit?: number | null;
  test_profiles?: string[];
  add_on?: number;
  enterprise_bundle?: boolean;
  crop_extraction_enabled?: boolean;
  account_type?: AccountTypeOption;
};

export type CustomerFormTypeCreate = CustomerFormBaseType & {
  admin_email: string;
  admin_first_name: string;
  admin_last_name: string;
  admin_password?: string;
};

export type CustomerFormTypeEdit = CustomerFormBaseType & {
  id?: number;
  user_deactivated_at?: string;
};

export type CustomerSummary = {
  users: number;
  admins: number;
  creators: number;
  viewers: number;
  suppliers: number;
  suppliers_creators: number;
};

export type CustomerCreateRequest = {
  company: string;
  admin_first_name: string;
  admin_last_name: string;
  admin_email: string;
  admin_password: string;
  plan_id: string;
  plan_effective_date: string;
  consumption_limit?: null | number;
  commodities: string[];
  add_on: number;
  enterprise_bundle: boolean;
  crop_extraction_enabled: boolean;
  account_type: AccountType;
  test_profiles: string[];
  custom_consumption_limit?: number | null;
};

export type CustomerEditRequest = {
  id: number;
  company?: string;
  plan_id?: string;
  plan_effective_date?: string;
  consumption_limit?: null | number;
  status?: UserStatusEnums;
  commodities?: string[];
  add_on?: number;
  enterprise_bundle?: boolean;
  crop_extraction_enabled?: boolean;
  account_type?: AccountType;
  test_profiles?: string[];
  custom_consumption_limit?: number | null;
};
